/**
 * UpdateBanner — Phase 3, incrément 3.2.
 *
 * Bandeau de mise à jour PWA. S'affiche en overlay top quand le
 * Service Worker détecte qu'une nouvelle version est prête (event
 * `nestor:sw-update` dispatché par `lib/sw-register.ts`).
 *
 * Cycle de vie :
 *   1. `mountUpdateBanner()` appelé une fois au boot par `main.ts`.
 *      Crée un container dédié dans `document.body` (HORS de l'AppShell
 *      pour survivre aux re-renders d'`AppShell`).
 *   2. Écoute `window.addEventListener('nestor:sw-update', …)`.
 *   3. Quand reçu : stocke la registration, met visible = true, render.
 *   4. Clic « Recharger » :
 *      - `registration.waiting.postMessage('SKIP_WAITING')` → le SW
 *        prend le contrôle.
 *      - `navigator.serviceWorker.controller` change → écoute
 *        `controllerchange` pour faire un reload propre une fois le
 *        SW activé.
 *   5. Clic « Plus tard » : visible = false, le bandeau disparaît
 *      pour la session courante. Sera re-déclenché si l'utilisateur
 *      reload manuellement et que la mise à jour n'a pas été appliquée
 *      (le SW v+1 est toujours en `waiting`).
 *
 * Ton bienveillant (CLAUDE.md §2.1) — pas de message anxiogène. Le
 * texte est en italique Fraunces (citation Nestor) sur fond sable.
 */
import { html, render, type TemplateResult } from 'lit-html';
import styles from './UpdateBanner.module.css';
import { t, subscribeLang } from '../i18n/index.js';
import { Icon, Icons } from './icons.js';
import { SW_UPDATE_EVENT, type SwUpdateEventDetail } from '../lib/sw-register.js';

interface BannerState {
  visible: boolean;
  registration: ServiceWorkerRegistration | null;
}

const state: BannerState = {
  visible: false,
  registration: null,
};

let container: HTMLElement | null = null;
let reloadRequested = false;

function bannerTemplate(): TemplateResult | null {
  if (!state.visible) return null;
  return html`<div class=${styles.banner} role="status" data-testid="update-banner">
    <span class=${styles.icon} aria-hidden="true">${Icon(Icons.spark, 16, { stroke: 1.6 })}</span>
    <span class=${styles.message}>«&nbsp;${t('sw_update_available')}&nbsp;»</span>
    <span class=${styles.actions}>
      <button
        type="button"
        class=${styles.btnSecondary}
        data-testid="update-banner-later"
        @click=${onLater}
      >
        ${t('sw_update_later')}
      </button>
      <button
        type="button"
        class=${styles.btnPrimary}
        data-testid="update-banner-reload"
        @click=${onReload}
      >
        ${t('sw_update_reload')}
      </button>
    </span>
  </div>`;
}

function rerender(): void {
  if (!container) return;
  const template = bannerTemplate();
  if (template) {
    render(template, container);
  } else {
    // Vide le container quand `visible === false` — préserve le
    // node lui-même pour les futurs re-renders.
    render(html``, container);
  }
}

function onLater(): void {
  state.visible = false;
  rerender();
}

function onReload(): void {
  const reg = state.registration;
  if (!reg) {
    // Fallback : pas de registration capturée. Reload simple.
    window.location.reload();
    return;
  }
  reloadRequested = true;
  const waiting = reg.waiting;
  if (waiting) {
    // Envoie le message au SW waiting pour qu'il prenne le contrôle.
    // Le `controllerchange` qui suit déclenchera notre reload.
    waiting.postMessage('SKIP_WAITING');
  } else {
    // Pas de SW waiting (cas rare : event reçu mais waiting est null
    // car déjà activé). Reload immédiat.
    window.location.reload();
  }
}

function onSwUpdate(event: Event): void {
  const detail = (event as CustomEvent<SwUpdateEventDetail>).detail;
  if (!detail?.registration) return;
  state.registration = detail.registration;
  state.visible = true;
  rerender();
}

function onControllerChange(): void {
  // Reload UNIQUEMENT si c'est nous qui avons demandé l'activation.
  // Sans ce flag, on rechargerait à la première registration (qui
  // n'est pas une mise à jour) — boucle infinie de reloads.
  if (!reloadRequested) return;
  window.location.reload();
}

/**
 * Monte le bandeau dans `document.body` et s'abonne aux événements
 * SW. Idempotent : appel multiple = no-op après le premier.
 *
 * Retourne une fonction de désinscription utile aux tests + au HMR
 * (sinon les listeners s'accumulent à chaque hot reload).
 */
export function mountUpdateBanner(): () => void {
  if (typeof document === 'undefined' || typeof window === 'undefined') {
    return () => undefined;
  }
  if (container) {
    // Déjà monté — retourne le teardown du précédent mount.
    return () => undefined;
  }
  container = document.createElement('div');
  container.setAttribute('data-testid', 'update-banner-root');
  document.body.appendChild(container);

  const swUpdateListener = (e: Event): void => onSwUpdate(e);
  window.addEventListener(SW_UPDATE_EVENT, swUpdateListener);

  // `controllerchange` se déclenche quand le nouveau SW active prend
  // le contrôle. On reload pour que le HTML/JS courant soit aligné
  // avec les nouveaux assets du cache.
  let removeControllerListener: () => void = () => undefined;
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.addEventListener('controllerchange', onControllerChange);
    removeControllerListener = (): void => {
      navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange);
    };
  }

  // Re-render au changement de langue — le bandeau lit `t(...)` qui
  // dépend de la langue courante.
  const unsubscribeLang = subscribeLang(() => {
    rerender();
  });

  return () => {
    window.removeEventListener(SW_UPDATE_EVENT, swUpdateListener);
    removeControllerListener();
    unsubscribeLang();
    if (container && container.parentNode) {
      container.parentNode.removeChild(container);
    }
    container = null;
    state.visible = false;
    state.registration = null;
    reloadRequested = false;
  };
}

/** @internal pour tests : simule la réception d'un event SW update. */
export function _triggerUpdateForTests(registration: ServiceWorkerRegistration): void {
  state.registration = registration;
  state.visible = true;
  rerender();
}

/** @internal pour tests : retourne l'état courant. */
export function _getUpdateBannerStateForTests(): BannerState {
  return { ...state };
}

/** @internal pour tests : reset complet. */
export function _resetUpdateBannerForTests(): void {
  state.visible = false;
  state.registration = null;
  reloadRequested = false;
  rerender();
}
